/**
 * ConfirmDestructive (§16.3): high-risk actions show the exact object, the consequence and the affected
 * participant count; the operator must type the object name before the action is enabled. Optional reason
 * (required for pause / cancel / redraw per Appendix A and §9.4).
 */
import { Button, Field, Input, Modal } from '@windrop/ui';
import { useEffect, useId, useState, type ReactNode } from 'react';

export interface DryRunResult {
  /** Number of participants (or objects) the action affects; null = the count could not be established. */
  affected: number | null;
  detail?: string;
}

export interface ConfirmDestructiveProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verb-plus-outcome, e.g. "Cancel lottery". */
  title: string;
  /** The exact object being acted on (slug, win ID, payout reference). The operator must type this. */
  objectName: string;
  /** What kind of thing `objectName` is, for the prompt ("lottery slug"). */
  objectKind?: string;
  /** Plain-language consequence; participant communication template name if any. */
  consequence: ReactNode;
  /** Affected participants; `null` while the dry run is loading, `undefined` when not applicable. */
  affected?: DryRunResult | null;
  /** Ask for a reason (stored in the audit entry). */
  requireReason?: boolean;
  reasonLabel?: string;
  confirmLabel?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (input: { reason?: string }) => void | Promise<void>;
}

export function ConfirmDestructive({
  open,
  onOpenChange,
  title,
  objectName,
  objectKind = 'name',
  consequence,
  affected,
  requireReason = false,
  reasonLabel = 'Reason (recorded in the audit trail)',
  confirmLabel,
  busy = false,
  error,
  onConfirm,
}: ConfirmDestructiveProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const reasonId = useId();
  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const nameMatches = typed.trim() === objectName;
  const reasonOk = !requireReason || reason.trim().length >= 3;
  const dryRunPending = affected === null;
  const canConfirm = nameMatches && reasonOk && !dryRunPending && !busy;
  const disabledReason = !nameMatches
    ? `Type the ${objectKind} exactly as shown to enable this action.`
    : !reasonOk
      ? 'A reason of at least 3 characters is required.'
      : dryRunPending
        ? 'Waiting for the affected count.'
        : undefined;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description="This cannot be undone.">
      <div className="wd-stack wdc-destructive">
        <dl className="wdc-facts">
          <div>
            <dt>Object</dt>
            <dd>
              <code data-testid="destructive-object">{objectName}</code>
            </dd>
          </div>
          <div>
            <dt>Consequence</dt>
            <dd>{consequence}</dd>
          </div>
          {affected !== undefined ? (
            <div>
              <dt>Affected participants</dt>
              <dd data-testid="destructive-affected" aria-live="polite">
                {affected === null
                  ? 'Counting…'
                  : `${affected.affected === null ? 'Unknown' : affected.affected}${affected.detail ? ` — ${affected.detail}` : ''}`}
              </dd>
            </div>
          ) : null}
        </dl>
        {requireReason ? (
          <Field label={reasonLabel} required id={reasonId}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        ) : null}
        <Field label={`Type the ${objectKind} to confirm`} required hint={<code>{objectName}</code>}>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="none"
            data-testid="destructive-typed"
          />
        </Field>
        {error ? (
          <p role="alert" className="text-error">
            {error}
          </p>
        ) : null}
        <div className="wd-btn-group">
          <Button
            variant="danger"
            disabled={!canConfirm}
            disabledReason={disabledReason}
            loading={busy}
            onClick={() => onConfirm({ reason: requireReason ? reason.trim() : undefined })}
          >
            {confirmLabel ?? title}
          </Button>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Keep as is
          </Button>
        </div>
      </div>
    </Modal>
  );
}
